import { useApiClient } from '@/api/ApiClient';
const { get2, get, getList, post } = useApiClient();

/**
 * 상세분석권 개수 조회
 *
 * 반환 예시:
 * ```json
 * {
 *   "count": 58,
 *   "tickets": [
 *     {
 *       "typeId": 3,
 *       "typeName": "성향분석",
 *       "totalCount": 54,
 *       "purchaseCount": 54,
 *       "eventCount": 0
 *     },
 *     {
 *       "typeId": 7,
 *       "typeName": "낭만일진",
 *       "totalCount": 4,
 *       "purchaseCount": 4,
 *       "eventCount": 0
 *     }
 *   ]
 * }
 * ```
 */
export const getTicketCountAPI = async () => {
  let path = `/analysis/ticket/count`;
  const { data } = await get(path, {});
  return data;
};

/**
 * 상세분석 이용내역 목록 조회
 *
 * 반환 예시:
 * ```json
 * {
 *   "content": [
 *     {
 *       "userId": 10924,
 *       "guestUserId": 24358,
 *       "analysisTypeId": 3,
 *       "analysisTicketId": null,
 *       "title": "",
 *       "description": "",
 *       "expiryDate": null,
 *       "year": 2025,
 *       "month": 6,
 *       "day": 26,
 *       "analysisLogId": 85,
 *       "createdAt": "2025-06-26T17:01:55",
 *       "updatedAt": "2025-06-26T17:01:55"
 *     }
 *   ],
 *   "pageable": {
 *     "page_number": 1,
 *     "page_size": 10,
 *     "offset": 0,
 *     "unpaged": false,
 *     "paged": true
 *   },
 *   "totalElements": 1,
 *   "size": 10,
 *   "number": 1,
 *   "totalPages": 1,
 *   "empty": false,
 *   "first": true,
 *   "last": true,
 *   "hasNext": false,
 *   "numberOfElements": 1,
 *   "sort": {
 *     "unsorted": true,
 *     "sorted": false,
 *     "empty": true
 *   }
 * }
 * ```
 */
export const getAnalysisLogAPI = async (params) => {
  let path = `/analysis/log`;
  const { data } = await get2(path, params);
  return data;
};

/* 분석권 결제 */
export const postAnalysisLogAPI = async (params) => {
  let path = `/analysis/log`;
  const data = await post(path, params);
  return data;
};

/**
 * 상세분석 접근 자격 조회 (저장사주 기준 일괄)
 *
 * 분석 이용내역 기반 권한과 구독 기반 권한을 함께 반영해, 저장사주 하나에 대해
 * 여러 분석 타입의 접근 가능 여부(canAccess)를 한 번에 반환한다.
 * 타입별로 /analysis/log 를 반복 호출하던 권한 확인을 대체한다.
 *
 * @param {Object} params
 * @param {number} params.guestUserId - 조회 대상 저장사주 ID (필수)
 * @param {number[]} [params.analysisTypeIds] - 조회할 분석 타입 ID 목록 (반복 query 로 전송)
 * @param {boolean} [params.includeChildTypes] - 직접 하위 타입도 item 에 포함할지 (기본 false)
 * @param {number} [params.year] - 조회 대상 연도 (월운 등 월 단위 접근 확인 시)
 * @param {number} [params.month] - 조회 대상 월
 * @param {number} [params.day] - 조회 대상 일
 *
 * 반환 예시:
 * ```json
 * {
 *   "guestUserId": 123,
 *   "items": [
 *     {
 *       "analysisTypeId": 3,
 *       "canAccess": true,
 *       "source": "ANALYSIS_LOG",  // NONE | ANALYSIS_LOG | SUBSCRIPTION | BOTH
 *       "analysisLogs": [{ "analysisLogId": 101, "expiryDate": "2026-06-30", ... }],
 *       "subscription": null
 *     }
 *   ]
 * }
 * ```
 */
export const getAnalysisAccessAPI = async (params) => {
  let path = `/analysis/access`;
  const { data } = await getList(path, params);
  return data;
};

/**
 * 상세분석 콘텐츠 상세 조회
 *
 * 반환 예시:
 * ```json
 * {
 *   "parentId": null,
 *   "name": "성향분석",
 *   "description": "",
 *   "code": null,
 *   "isFixed": false,
 *   "isActive": true,
 *   "pointPrice": 770,
 *   "pointBasePrice": 770,
 *   "split": "NONE",
 *   "analysisTypeId": 3,
 *   "createdAt": "2025-02-03T16:46:10",
 *   "updatedAt": "2025-02-03T16:46:10",
 *   "parent": null,
 *   "children": []
 * }
 * ```
 */
export const getAnalysisContentAPI = async (params) => {
  let path = `/analysis/type/${params.analysisTypeId}`;
  const { data } = await get(path, {});
  return data;
};

/**
 * 상세분석 콘텐츠 목록 조회
 *
 * 부모(analysisTypeId) 또는 자식(parentId) 단위로 리스트를 받아온다.
 * 낭만월운 자식 카드 가격 조회에 사용. (parentId=10 → 자식 5개 carousel)
 *
 * @param {Object} params - { analysisTypeId | parentId, isActive, page, size }
 */
export const getAnalysisContentListAPI = async (params) => {
  let path = `/analysis/type`;
  const { data } = await get2(path, params);
  return data;
};

/**
 * 분석권 목록 조회
 */
export const getAnalysisTicketAPI = async (params) => {
  let path = `/analysis/ticket`;
  const { data } = await get(path, params);
  return data;
};
